import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Flag, CheckCircle, XCircle, Eye, AlertTriangle } from 'lucide-react';

const STATUS_MAP = {
  pending:      { label: 'Pending',      cls: 'badge-warning' },
  reviewed:     { label: 'Reviewed',     cls: 'badge-info'    },
  action_taken: { label: 'Action Taken', cls: 'badge-success' },
  dismissed:    { label: 'Dismissed',    cls: 'badge-neutral' },
};

const REASON_LABELS = {
  fake_product:          'Fake / Misleading product',
  wrong_price:           'Wrong price',
  spam:                  'Spam',
  inappropriate_content: 'Inappropriate content',
  scam:                  'Possible scam',
  abusive_behavior:      'Abusive behavior',
  prohibited_item:       'Prohibited item',
  other:                 'Other',
};

export default function AdminReports() {
  const [reports,    setReports]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [pending,    setPending]    = useState(0);
  const [total,      setTotal]      = useState(0);
  const [status,     setStatus]     = useState('all');
  const [targetType, setTargetType] = useState('all');
  const [page,       setPage]       = useState(1);
  const [pages,      setPages]      = useState(1);

  // Selected report for detail view
  const [selected,   setSelected]   = useState(null);
  const [adminNote,  setAdminNote]  = useState('');
  const [updating,   setUpdating]   = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (status     !== 'all') params.set('status', status);
      if (targetType !== 'all') params.set('targetType', targetType);
      const { data } = await api.get('/admin/reports?' + params.toString());
      setReports(data.reports);
      setPending(data.pending);
      setTotal(data.total);
      setPages(data.pages || 1);
    } catch {
      toast.error('Could not load reports.');
    } finally {
      setLoading(false);
    }
  }, [status, targetType, page]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleUpdate = async (id, newStatus) => {
    setUpdating(true);
    try {
      const { data } = await api.put('/admin/reports/' + id, {
        status:    newStatus,
        adminNote: adminNote,
      });
      setReports(prev => prev.map(r => r._id === id ? data.report : r));
      if (selected?._id === id) setSelected(data.report);
      if (newStatus === 'pending') setPending(p => p - 1);
      toast.success('Report updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update.');
    } finally {
      setUpdating(false);
    }
  };

  const openReport = (report) => {
    setSelected(report);
    setAdminNote(report.adminNote || '');
  };

  return (
    <div className="fade-in">

      {/* Header */}
      <div className="admin-page-header">
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            Reports
            {pending > 0 && (
              <span style={{
                background: 'var(--danger)', color: '#fff',
                fontSize: '0.75rem', fontWeight: 700,
                padding: '3px 10px', borderRadius: 999,
              }}>
                {pending} pending
              </span>
            )}
          </h2>
          <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 4 }}>
            {total} total report{total !== 1 ? 's' : ''} · Review and take action
          </p>
        </div>
      </div>

      {/* Layout: table + detail panel */}
      <div style={{ display: 'flex', gap: 'var(--space-5)', alignItems: 'flex-start' }}>

        {/* Left: filters + table */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Filters */}
          <div className="admin-controls">
            <select className="select" style={{ width: 150 }}
              value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
              <option value="action_taken">Action Taken</option>
              <option value="dismissed">Dismissed</option>
            </select>

            <select className="select" style={{ width: 150 }}
              value={targetType} onChange={e => { setTargetType(e.target.value); setPage(1); }}>
              <option value="all">All Types</option>
              <option value="product">Products</option>
              <option value="user">Users</option>
            </select>
          </div>

          {/* Table */}
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Reported Item</th>
                  <th>Reason</th>
                  <th>Reported By</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40 }}>
                      <div className="spinner" style={{ margin: '0 auto' }} />
                    </td>
                  </tr>
                ) : reports.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                      No reports found
                    </td>
                  </tr>
                ) : reports.map(r => (
                  <tr
                    key={r._id}
                    style={{ cursor: 'pointer', background: selected?._id === r._id ? 'var(--accent-soft)' : '' }}
                    onClick={() => openReport(r)}
                  >
                    {/* Type */}
                    <td>
                      <span className={'badge ' + (r.targetType === 'product' ? 'badge-info' : 'badge-warning')}>
                        {r.targetType === 'product' ? '📦 Product' : '👤 User'}
                      </span>
                    </td>

                    {/* Item */}
                    <td>
                      {r.targetType === 'product' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {r.productSnapshot?.image && (
                            <img src={r.productSnapshot.image} alt=""
                              style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                          )}
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>
                              {r.productSnapshot?.title || '—'}
                            </div>
                            {r.productSnapshot?.price && (
                              <div style={{ fontSize: '0.72rem', color: 'var(--accent)' }}>
                                ₹{r.productSnapshot.price.toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>
                            {r.reportedUserSnapshot?.name || '—'}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--accent)' }}>
                            @{r.reportedUserSnapshot?.username || '—'}
                          </div>
                        </div>
                      )}
                    </td>

                    {/* Reason */}
                    <td style={{ fontSize: '0.8rem' }}>
                      {REASON_LABELS[r.reason] || r.reason}
                    </td>

                    {/* Reporter */}
                    <td style={{ fontSize: '0.8rem' }}>
                      <div>{r.reporterSnapshot?.name}</div>
                      <div style={{ color: 'var(--accent)', fontSize: '0.72rem' }}>
                        @{r.reporterSnapshot?.username}
                      </div>
                    </td>

                    {/* Date */}
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {new Date(r.createdAt).toLocaleDateString('en-IN', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </td>

                    {/* Status */}
                    <td>
                      <span className={'badge ' + (STATUS_MAP[r.status]?.cls || 'badge-neutral')}>
                        {STATUS_MAP[r.status]?.label || r.status}
                      </span>
                    </td>

                    <td>
                      <Eye size={15} style={{ color: 'var(--text-muted)' }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
              <button className="btn btn-secondary btn-sm"
                disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span style={{ lineHeight: '36px', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                {page} / {pages}
              </span>
              <button className="btn btn-secondary btn-sm"
                disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </div>

        {/* Right: detail panel */}
        {selected && (
          <div style={{
            width: 320, flexShrink: 0,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-5)',
            display: 'flex', flexDirection: 'column', gap: 'var(--space-4)',
            position: 'sticky', top: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h4 style={{ fontFamily: 'var(--font-body)', fontWeight: 700 }}>Report Detail</h4>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setSelected(null)}>✕</button>
            </div>

            {/* Type + Reason */}
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 'var(--space-4)' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-light)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                Report Type
              </div>
              <div style={{ fontWeight: 600 }}>
                {selected.targetType === 'product' ? '📦 Product Report' : '👤 User Report'}
              </div>
              <div style={{ marginTop: 8, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                <strong>Reason:</strong> {REASON_LABELS[selected.reason] || selected.reason}
              </div>
              {selected.details && (
                <div style={{
                  marginTop: 8, fontSize: '0.82rem', fontStyle: 'italic',
                  color: 'var(--text-muted)', background: 'var(--bg-2)',
                  borderRadius: 'var(--radius-sm)', padding: '6px 10px',
                }}>
                  "{selected.details}"
                </div>
              )}
            </div>

            {/* Reported item */}
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 'var(--space-4)' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-light)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                {selected.targetType === 'product' ? 'Reported Product' : 'Reported User'}
              </div>
              {selected.targetType === 'product' ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {selected.productSnapshot?.image && (
                    <img src={selected.productSnapshot.image} alt=""
                      style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover' }} />
                  )}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {selected.productSnapshot?.title}
                    </div>
                    <div style={{ color: 'var(--accent)', fontSize: '0.8rem' }}>
                      ₹{selected.productSnapshot?.price?.toLocaleString()}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 600 }}>{selected.reportedUserSnapshot?.name}</div>
                  <div style={{ color: 'var(--accent)', fontSize: '0.82rem' }}>
                    @{selected.reportedUserSnapshot?.username}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {selected.reportedUserSnapshot?.email}
                  </div>
                </div>
              )}
            </div>

            {/* Reporter */}
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 'var(--space-4)' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-light)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                Reported By
              </div>
              <div style={{ fontWeight: 600 }}>{selected.reporterSnapshot?.name}</div>
              <div style={{ color: 'var(--accent)', fontSize: '0.82rem' }}>@{selected.reporterSnapshot?.username}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{selected.reporterSnapshot?.email}</div>
            </div>

            {/* Admin note */}
            <div className="form-group">
              <label className="label">Admin Note</label>
              <textarea
                className="textarea"
                placeholder="Add a note about this report..."
                value={adminNote}
                onChange={e => setAdminNote(e.target.value)}
                rows={3}
                maxLength={500}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {selected.status === 'pending' && (
                <>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleUpdate(selected._id, 'reviewed')}
                    disabled={updating}
                  >
                    <CheckCircle size={14} /> Mark as Reviewed
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleUpdate(selected._id, 'action_taken')}
                    disabled={updating}
                  >
                    <AlertTriangle size={14} /> Action Taken
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleUpdate(selected._id, 'dismissed')}
                    disabled={updating}
                  >
                    <XCircle size={14} /> Dismiss Report
                  </button>
                </>
              )}
              {selected.status !== 'pending' && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleUpdate(selected._id, selected.status)}
                  disabled={updating}
                >
                  {updating
                    ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving...</>
                    : <><CheckCircle size={14} /> Save Note</>
                  }
                </button>
              )}
              <div style={{ fontSize: '0.72rem', color: 'var(--text-light)', textAlign: 'center' }}>
                Current: <strong>{STATUS_MAP[selected.status]?.label}</strong>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}